// React
import React, { useState } from 'react';

// FutureLab
import GroupDetails from '../../popup/groupDetails';
import { isChatApproved } from '../../../utilities/Chat';
import { ModalConfirmation } from '../../popup/modalConfirmation';
import { ChatListDataInterface } from '../../chat/ChatListItemsDesktop';
import { UserDetailsListItem } from '../../../model';
import { LoggedInUserDetails } from '../../../reducers';
import { isSameUserId, toUserId } from '../../../utilities/UserId';

// UI
import { ArrowLeft, MoreVertical } from 'lucide-react';
import { Avatar, AvatarFallback } from '../../ui/avatar';
import { Button } from '../../ui/button';
import {
	DropdownMenu,
	DropdownMenuContent,
	DropdownMenuItem,
	DropdownMenuTrigger,
} from '../../ui/dropdown-menu';
import { OptionStoreItem } from '../../chat/DesktopChatRoomHeader';

interface GroupChatTopNavBarProps {
	chatInFocus: ChatListDataInterface;
	loggedInUserDetails: LoggedInUserDetails;
	userListProfilesDetails: UserDetailsListItem[];
	goToChatPage: Function;
	displayModalConfirmation: boolean;
	setDisplayModalConfirmation: any;
	optionSelectorChatMessages: Function;
	optionStore: OptionStoreItem;
	setOptionStore: any;
	optionsGroupChat: OptionStoreItem[];
}

export default function GroupChatTopNavBar(groupChatTopNavBarProps: GroupChatTopNavBarProps) {
	const {
		chatInFocus,
		loggedInUserDetails,
		userListProfilesDetails,
		goToChatPage,
		displayModalConfirmation,
		setDisplayModalConfirmation,
		optionSelectorChatMessages,
		optionStore,
		setOptionStore,
		optionsGroupChat,
	} = groupChatTopNavBarProps;

	const [displayGroupDetails, setDisplayGroupDetails] = useState<boolean>(false);
	const [menuOpen, setMenuOpen] = useState(false);
	const handleClose = () => setMenuOpen(false);

	const openPopUp = (option: string) => {
		setDisplayModalConfirmation(!displayModalConfirmation);
		handleClose();
	};

	const checkGroupOption = chatInFocus?.chatAdmins?.some(
		(participant) => isSameUserId(participant.userId, toUserId(loggedInUserDetails.id))
	)
		? optionsGroupChat
		: [optionsGroupChat[1]];

	const getGroupChatNameFromParticipantNameInitials = (): string => {
		const groupParticipantIds = chatInFocus.participants.map((participant) => participant.userId);
		const userListProfilesFullDetails = userListProfilesDetails.filter((userListProfileDetails) =>
			groupParticipantIds.some((userId) => isSameUserId(userId, userListProfileDetails.id))
		);
		return userListProfilesFullDetails[0]?.firstname?.charAt(0) || '?';
	};

	const getGroupChatNameInitials = (): string => {
		const nameFromDb = chatInFocus?.name?.trim()?.charAt(0);
		return nameFromDb ? nameFromDb : getGroupChatNameFromParticipantNameInitials();
	};

	const getGroupChatNameFromParticipantNames = (): string => {
		const groupParticipantIds = chatInFocus.participants.map((participant) => participant.userId);
		const userListProfilesFullDetails = userListProfilesDetails.filter((userListProfileDetails) =>
			groupParticipantIds.some((userId) => isSameUserId(userId, userListProfileDetails.id))
		);
		return userListProfilesFullDetails
			.map((user) => user.firstname?.trim())
			.filter(Boolean)
			.join(', ') || 'Unnamed group';
	};

	const renderGroupOption = () => {
		return checkGroupOption.map((optionGroupChat) => (
			<DropdownMenuItem
				key={optionGroupChat.option}
				className="justify-center"
				onClick={() => {
					setOptionStore(optionGroupChat);
					openPopUp(optionGroupChat.option);
				}}
			>
					{optionGroupChat.option}
			</DropdownMenuItem>
		));
	};

	const getGroupChatName = (): string => {
		const nameFromDb = chatInFocus.name;
		return nameFromDb ? nameFromDb : getGroupChatNameFromParticipantNames();
	};

	return (
		<div className="flex h-[60px] w-full min-w-0 items-center overflow-x-hidden border-b-2 border-solid bg-white px-1">
			<div className="flex w-11 shrink-0 justify-center p-0 text-center">
				<Button variant="ghost" size="icon" onClick={() => goToChatPage()}>
					<ArrowLeft className="size-5 text-[#575757]" />
					<span className="sr-only">Back to chats</span>
				</Button>
			</div>
			<div className="w-14 shrink-0 text-center">
				<Avatar
					className="mx-auto cursor-pointer"
					onClick={() => {
						setDisplayGroupDetails(!displayGroupDetails);
					}}
				>
					<AvatarFallback>{getGroupChatNameInitials().toLocaleUpperCase()}</AvatarFallback>
				</Avatar>
			</div>
			{displayGroupDetails && (
				<GroupDetails
					setDisplayGroupDetails={setDisplayGroupDetails}
					displayGroupDetails={displayGroupDetails}
					getGroupChatNameInitials={getGroupChatNameInitials}
					getGroupChatName={getGroupChatName}
				/>
			)}
			<div className="min-w-0 flex-1 px-1">
				<div className="truncate capitalize text-base">{getGroupChatName()}</div>
				<div className="text-xxs italic">{chatInFocus.participants.length} members</div>
			</div>
			<div className="flex w-11 shrink-0 justify-center">
				{isChatApproved(chatInFocus, loggedInUserDetails?.id) && (
					<DropdownMenu open={menuOpen} onOpenChange={setMenuOpen}>
						<DropdownMenuTrigger render={<Button variant="ghost" size="icon" />}>
							<MoreVertical className="size-5 text-[#575757]" />
							<span className="sr-only">Chat options</span>
						</DropdownMenuTrigger>
						<DropdownMenuContent align="end" className="w-40 text-center">
							{renderGroupOption()}
						</DropdownMenuContent>
					</DropdownMenu>
				)}
			</div>
			{displayModalConfirmation && (
				<ModalConfirmation
					displayModalConfirmation={displayModalConfirmation}
					setDisplayModalConfirmation={setDisplayModalConfirmation}
					optionStore={optionStore}
					callBackProceedMethodProp={() => optionSelectorChatMessages(optionStore?.option)}
				/>
			)}
		</div>
	);
}
